/**
 * Forgot Password Screen
 * 
 * Allows users to request a password reset email.
 * Uses Firebase Auth sendPasswordResetEmail.
 * 
 * @module components/auth/ForgotPasswordScreen
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@sequelae/lore-core';
import { validate as emailValidator } from 'email-validator';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import './ForgotPasswordScreen.css';

export const ForgotPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Form state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Result state
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Redirect to reset password screen if this URL has mode=resetPassword
   * This handles old Firebase reset links that point to /forgot-password
   */
  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    
    if (mode === 'resetPassword' && oobCode) {
      console.log('[ForgotPassword] Detected reset password link, redirecting to /reset-password');
      navigate(`/reset-password?${searchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  /**
   * Validate email format
   */
  const validateEmail = useCallback((value: string): string => {
    if (!value) {
      return 'Email is required.';
    }
    if (!emailValidator(value)) {
      return 'Please enter a valid email address.';
    }
    return '';
  }, []);

  /**
   * Handle email input change
   */
  const handleEmailChange = (value: string) => {
    setEmail(value);
    const error = validateEmail(value);
    setEmailError(error);
    // Clear any previous results when user starts typing again
    if (successMessage || errorMessage) {
      setSuccessMessage('');
      setErrorMessage('');
    }
  };

  /**
   * Check if form is valid for submission
   */
  const isFormValid = (): boolean => {
    return email.length > 0 && emailValidator(email) && !isSubmitting;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      console.log('[ForgotPassword] Sending password reset email to:', email);
      
      const auth = getFirebaseAuth();
      
      // Send password reset email using Firebase default configuration
      // Landing page will redirect web users to:
      //   - Production: app.lore.co
      //   - Non-prod: app.lorehealthcare.com
      // Native apps will intercept the link before browser opens
      await sendPasswordResetEmail(auth, email);
      
      console.log('[ForgotPassword] Password reset email sent successfully');
      setSuccessMessage('Password reset email sent. Please check your inbox and follow the instructions to reset your password.');
      
    } catch (error: any) {
      console.error('[ForgotPassword] Failed to send reset email:', error);
      
      if (error.code === 'auth/invalid-email') {
        setErrorMessage('The email address is not valid.');
      } else if (error.code === 'auth/too-many-requests') {
        setErrorMessage('Too many attempts. Please try again later.');
      } else {
        setErrorMessage('Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle close button / back to sign in
   */
  const handleClose = () => {
    navigate('/sign-in');
  };

  /**
   * Handle back to auth landing
   */
  const handleBackToAuth = () => {
    navigate('/auth');
  };

  return (
    <div className="forgot-password-screen">
      <div className="forgot-password-card">
        {/* Header with title and close button */}
        <header className="forgot-password-header">
          <h1 className="forgot-password-title">Forgot your password?</h1>
          <button 
            className="forgot-password-close-btn" 
            onClick={handleClose}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Subtitle */}
        <p className="forgot-password-subtitle">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="forgot-password-form">
          {/* Email Field */}
          <div className="forgot-password-field-group">
            <label htmlFor="forgot-email" className="forgot-password-field-label">
              Email Address
            </label>
            <input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="email@example.com"
              disabled={isSubmitting}
              className={`forgot-password-field-input ${emailError ? 'has-error' : ''}`}
              autoComplete="email"
              autoFocus
            />
            {emailError && <p className="forgot-password-field-error">{emailError}</p>}
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="forgot-password-success">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <div className="forgot-password-success-content">
                <p className="forgot-password-success-title">Email Sent!</p>
                <p className="forgot-password-success-message">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="forgot-password-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <div className="forgot-password-error-content">
                <p className="forgot-password-error-title">Unable to Send Email</p>
                <p className="forgot-password-error-message">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? <Spinner /> : 'Send Reset Email'}
          </Button>

          {/* Back to Sign In Link */}
          <button
            type="button"
            className="forgot-password-back-link"
            onClick={handleBackToAuth}
            disabled={isSubmitting}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

